import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { FaClock, FaCheck, FaHandPaper, FaVideo, FaTimes } from "react-icons/fa";
import { SkeletonRoom } from "../../Skeleton";
import API_URL from '../../../utils/api';
import "./BreakoutRoom.css";
export default function BreakoutRoom() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [myPair, setMyPair] = useState(null);
  const [myPairIndex, setMyPairIndex] = useState(null);
  const [myPairKey, setMyPairKey] = useState(null);
  const [currentRound, setCurrentRound] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentSpeakerIndex, setCurrentSpeakerIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(null); // Will be set from backend
  const [userId] = useState(() => sessionStorage.getItem("userId") || localStorage.getItem("userId"));
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [breakoutPrompts, setBreakoutPrompts] = useState([]);
  const socketRef = useRef(null);
  const myPairRef = useRef(null); // keeps myPair accessible inside socket closure
  
  // Helper to create consistent pair key
  const getPairKey = (pair) => {
    const ids = pair.map(m => String(m.user_id || m.userId)).sort();
    return ids.join('-');
  };

  const showNotification = useCallback((next) => {
    setNotification(next);
    if (next) {
      setTimeout(() => setNotification(null), 3000);
    }
  }, []);

  // Fetch session data
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await fetch(`${API_URL}/api/sessions/${sessionId}`);
        const data = await response.json();
        if (data.members && typeof data.members === 'string') {
          data.members = JSON.parse(data.members);
        }
        setSession(data);
      } catch (error) {
        console.error('[BreakoutRoom] Failed to fetch session:', error);
        showNotification({ type: 'error', message: 'Failed to load session.' });
      }
    };
    fetchSession();
  }, [sessionId, showNotification]);

  // Fetch AI-generated breakout questions for this session
  useEffect(() => {
    const fetchQuestions = async () => {
      const fallback = [
        "What's a dream you've had since childhood? Why does it matter to you?",
        "Describe a moment when you felt most alive. What were you doing?",
        "What makes you feel most connected to others?",
        "What are you most afraid of in relationships or deep connections?",
        "How do you show love or care to the people who matter to you?"
      ];
      try {
        const res = await fetch(`${API_URL}/api/sessions/${sessionId}/ai-questions`);
        const data = await res.json();
        if (data.breakout && data.breakout.length > 0) {
          setBreakoutPrompts(data.breakout);
        } else {
          setBreakoutPrompts(fallback);
        }
      } catch {
        setBreakoutPrompts(fallback);
      }
    };
    fetchQuestions();
  }, [sessionId]);
  // Initialize breakout rooms
  useEffect(() => {
    const startBreakout = async () => {
      try {
        const response = await fetch(`${API_URL}/api/sessions/${sessionId}/start-breakout`, {
          method: 'POST',
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ startTimer: true, userId })
        });
        const data = await response.json();
        if (data.ok && data.pairs) {
          // Find my pair - check both user_id and userId fields
          const pairIndex = data.pairs.findIndex(p => {
            return p.some(member => {
              const memberId = String(member.user_id || member.userId || '');
              const currentUserId = String(userId || '');
              return memberId === currentUserId;
            });
          });
          
          if (pairIndex !== -1) {
            const pair = data.pairs[pairIndex];
            setMyPair(pair);
            setMyPairIndex(pairIndex);
            setMyPairKey(getPairKey(pair));
            myPairRef.current = pair;
            setCurrentRound(data.round);
            setIsLoading(false);
            // Set timer from backend if available
            if (data.timeRemaining !== undefined) {
              setTimeRemaining(data.timeRemaining);
            } else {
            }
          } else {
            // If no pair found, still set something to exit loading state
            setMyPair(data.pairs[0]); // Fallback to first pair
            setMyPairIndex(0);
            setMyPairKey(getPairKey(data.pairs[0]));
            myPairRef.current = data.pairs[0];
            setIsLoading(false);
            // Set timer from backend if available
            if (data.timeRemaining !== undefined) {
              setTimeRemaining(data.timeRemaining);
            }
          }
        } else {
          showNotification({ type: 'error', message: 'Failed to start breakout.' });
        }
      } catch (error) {
        console.error('[BreakoutRoom] Failed to start breakout:', error);
        showNotification({ type: 'error', message: 'Failed to start breakout.' });
      }
    };
    if (session) {
      startBreakout();
    }
  }, [session, sessionId, userId, showNotification]);
  // Socket.io for real-time updates
  useEffect(() => {
    if (!socketRef.current) {
      socketRef.current = io(API_URL, { transports: ["websocket"] });
      socketRef.current.on("connect", () => {
        console.log('[BreakoutRoom] Socket connected');
      });
      // Listen for breakout start
      socketRef.current.on(`session-${sessionId}-breakout-start`, (data) => {
        const pairIndex = data.pairs.findIndex(p => 
          p.some(member => {
            const memberId = String(member.user_id || member.userId || '');
            const currentUserId = String(userId || '');
            return memberId === currentUserId;
          })
        );
        if (pairIndex !== -1) {
          const pair = data.pairs[pairIndex];
          setMyPair(pair);
          setMyPairIndex(pairIndex);
          setMyPairKey(getPairKey(pair));
          setCurrentRound(data.round);
          // Set timer from socket event if available
          if (data.timeRemaining !== undefined) {
            setTimeRemaining(data.timeRemaining);
          }
        }
      });
      // Listen for breakout progression (turn changes) - filter by pair
      socketRef.current.on(`session-${sessionId}-breakout-progress`, (data) => {
        // Only update if this progress event is for our pair
        // Use pairKey for comparison since myPairIndex might not be set yet in closure
        setMyPairKey(currentKey => {
          if (data.pairKey === currentKey) {
            setCurrentQuestionIndex(data.questionIndex);
            setCurrentSpeakerIndex(data.speakerIndex);
          }
          return currentKey;
        });
      });

      // Listen for a participant leaving — only react if it's our pair partner
      socketRef.current.on(`session-${sessionId}-user-left`, (data) => {
        const leavingId = String(data?.user?.user_id || data?.user?.userId || '');
        if (!leavingId) return;
        const pair = myPairRef.current;
        if (!pair) return;
        // Check if the person who left is in our pair (and is not us)
        const isOurPartner = pair.some(m =>
          String(m.user_id ?? m.userId) === leavingId &&
          String(m.user_id ?? m.userId) !== String(userId)
        );
        if (!isOurPartner) return; // someone from the other group — ignore
        const name = data?.user?.name || 'Your partner';
        showNotification({ type: 'leave', message: `${name} left the session` });
        // Redirect this user back to dashboard after 3 seconds
        setTimeout(() => navigate('/dashboard'), 3000);
      });
    }
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [sessionId, userId]);
  // Timer countdown
  useEffect(() => {
    if (timeRemaining !== null && timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0) {
      // Time's up - navigate to post-breakout evaluation
      setTimeout(() => {
        navigate(`/post-breakout-evaluation/${sessionId}`);
      }, 2000); // Give 2 seconds to see the time's up message
    }
  }, [timeRemaining, navigate, sessionId]);
  // Handle breakout completion (all questions answered)
  useEffect(() => {
    if (breakoutPrompts.length > 0 && currentQuestionIndex >= breakoutPrompts.length) {
      const timer = setTimeout(() => {
        navigate(`/post-breakout-evaluation/${sessionId}`);
      }, 3000); // Give 3 seconds to see the completion message
      return () => clearTimeout(timer);
    }
  }, [currentQuestionIndex, breakoutPrompts.length, navigate, sessionId]);
  const handleDone = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sessions/${sessionId}/breakout-room/done`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, questionIndex: currentQuestionIndex })
      });
      const data = await response.json();
      if (data.ok) {
        // Socket event will update the UI
      } else {
        showNotification({ type: 'warning', message: data.message || "It's not your turn to share yet!" });
      }
    } catch (error) {
      console.error('[BreakoutRoom] Failed to mark done:', error);
      showNotification({ type: 'error', message: 'Failed to submit your turn.' });
    }
  };
  const handleLeaveSession = async () => {
    const confirmLeave = window.confirm('Are you sure you want to leave this breakout session?');
    if (!confirmLeave) return;
    try {
      const userName = session.members?.find(m => String(m.user_id ?? m.userId) === String(userId))?.name;
      const response = await fetch(`${API_URL}/api/sessions/${sessionId}/leave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          userId,
          userName
        })
      });
      const data = await response.json();
      if (data.ok) {
        // Untrack before leaving
        if (socketRef.current) {
          socketRef.current.emit('untrack-session');
        }
        navigate('/dashboard');
      } else {
        alert(data.message || 'Failed to leave session');
      }
    } catch (error) {
      alert('Failed to leave session: ' + error.message);
    }
  };
  if (!session || !myPair || isLoading) {
    return (
      <div className="breakout-room-root">
        <SkeletonRoom />
      </div>
    );
  }
  const partner = myPair.find(member => {
    const memberId = String(member.user_id || member.userId || '');
    const currentUserId = String(userId || '');
    return memberId !== currentUserId;
  });
  const minutes = Math.floor((timeRemaining || 0) / 60);
  const seconds = (timeRemaining || 0) % 60;
  const currentPrompt = breakoutPrompts[currentQuestionIndex] || breakoutPrompts[0] || "Loading question...";
  const currentSpeaker = myPair[currentSpeakerIndex];
  const isMyTurn = String(currentSpeaker?.user_id || currentSpeaker?.userId) === String(userId);
  const isBreakoutComplete = currentQuestionIndex >= breakoutPrompts.length;
  return (
    <div className="breakout-room-root">
      {/* Notification Toast */}
      {notification && (
        <div className={`breakout-notification ${notification.type}`}>
          {notification.type === 'leave' && <FaHandPaper /> }
          {notification.message}
        </div>
      )}
      {/* Breakout Complete Message */}
      {isBreakoutComplete && (
        <div className="breakout-complete-message">
          <div className="complete-icon"><FaCheck /></div>
          <h2>Breakout Session Complete!</h2>
          <p>Great conversation! You'll be redirected shortly...</p>
        </div>
      )}
      {!isBreakoutComplete && (
        <>
          {/* Compact Header Bar */}
          <div className="breakout-header-bar">
            <div className="header-left">
              <div className="question-counter">Question {currentQuestionIndex + 1}/{breakoutPrompts.length}</div>
              <div className="question-counter">Round {currentRound}</div>
              <a 
                href={myPair?.[0]?.meet_link || myPair?.[1]?.meet_link || session.meet_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="compact-meet-link"
                onClick={(e) => { e.preventDefault(); const link = myPair?.[0]?.meet_link || myPair?.[1]?.meet_link || session?.meet_link; if (link) window.open(link, '_blank', 'noopener,noreferrer'); }}
              >
                <FaVideo /> Join Meet
              </a>
            </div>
            <div className="header-right">
              <div className="partner-info-compact">
                <div className="partner-avatar-tiny">
                  {partner?.name?.charAt(0).toUpperCase()}
                </div>
                <span className="partner-name-tiny">{partner?.name}</span>
              </div>
              <div className="breakout-timer-compact">
                ⏱ {minutes}:{seconds.toString().padStart(2, '0')}
              </div>
              <button 
                className="breakout-leave-btn-compact"
                onClick={handleLeaveSession}
                title="Leave Session"
              >
                <FaTimes />
              </button>
            </div>
          </div>
          {/* Turn Indicator - Compact */}
          <div className="turn-indicator-wrapper">
            <div className={`turn-indicator-compact ${isMyTurn ? 'my-turn' : 'partner-turn'}`}>
              {isMyTurn ? (
                <span>▶ Your turn to share</span>
              ) : (
                <span>● {currentSpeaker?.name} is sharing</span>
              )}
            </div>
          </div>
          {/* Discussion Prompt - Main Focus */}
          <div className="breakout-prompt-card">
            <div className="prompt-label">Discussion Topic:</div>
            <div className="breakout-prompt-text">{currentPrompt}</div>
          </div>
          {/* Done Button */}
          {isMyTurn && (
            <div className="breakout-done-section">
              <button 
                className="breakout-done-btn"
                onClick={handleDone}
              >
                I'm Done Sharing →
              </button>
            </div>
          )}
          {timeRemaining === 0 && (
            <div className="time-up-message">
              Time's up! Please wrap up your conversation.
            </div>
          )}
        </>
      )}
    </div>
  );
}
